import React, { useEffect } from "react";
import { Row, Col } from "react-flexbox-grid/lib/index";
import L from "leaflet";
import { MapContainer, TileLayer, useMap } from "react-leaflet";
import PropTypes from "prop-types";
import "leaflet-draw";

const MapSearchControls = ({ siteData, changeChip, siteIDOptions }) => {
  const map = useMap();

  // return array containing site names falling within drawn rectangle bounds
  // because we need the names of sites falling within the drawn bounds in order
  // to update the query
  const filterSitesWithinBounds = bounds => {
    const sw = bounds[0][0];
    const ne = bounds[0][2];

    let filteredSites = Object.values(siteData).filter(site => {
      return (
        site.latitude >= sw.lat &&
        site.latitude <= ne.lat &&
        site.longitude >= sw.lng &&
        site.longitude <= ne.lng
      );
    });

    filteredSites = filteredSites.map(site => site.name);

    return filteredSites;
  };

  useEffect(() => {
    // flag to prevent async actions firing after component unmounts
    let isMounted = true;

    // Create a feature group layer to store drawn rectangle
    const searchBounds = new L.FeatureGroup();
    map.addLayer(searchBounds);

    const drawOptions = {
      draw: {
        // disable toolbar item by setting it to false
        polyline: false,
        polygon: false,
        circle: false,
        marker: false,
        circlemarker: false,
        rectangle: true
      },
      edit: {
        featureGroup: searchBounds,
        remove: false,
        edit: false
      }
    };

    // Initialize the draw control and pass it the FeatureGroup of editable layers
    const drawControl = new L.Control.Draw(drawOptions);
    map.addControl(drawControl);

    // Event handler for when a rectangle is drawn
    const handleDrawCreated = e => {
      searchBounds.clearLayers();
      // get bounds of the drawn rectangle and use it to fit map view around the boundary
      const bounds = e.layer.getBounds();

      L.rectangle(bounds).addTo(searchBounds);

      map.fitBounds(bounds, { padding: [50, 50] });

      // get the coordinates of the drawn rectangle and use it to filter sites inside the rectangle
      const latLngBounds = e.layer.getLatLngs();
      let filteredSites = filterSitesWithinBounds(latLngBounds);

      filteredSites = filteredSites.filter(site =>
        siteIDOptions.includes(site)
      );

      // changeChip calls async functions
      // may attempt to call async actions after component unmount == memory leak warning
      if (isMounted) {
        changeChip("site_id", filteredSites);
      }
    };

    // Clear all rectangles and disable drawing tool on map click
    const handleMapClick = () => {
      searchBounds.clearLayers();
    };

    // Add event listeners to the map
    map.on("click", handleMapClick);
    map.on("draw:created", handleDrawCreated);

    // Cleanup function to remove event listener and layers on component unmount
    return () => {
      isMounted = false;
      map.off("click", handleMapClick);
      map.off("draw:created", handleDrawCreated);
      map.removeLayer(searchBounds);
    };
  }, [map]);

  return null;
};

MapSearchControls.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  siteData: PropTypes.object.isRequired,
  changeChip: PropTypes.func.isRequired,
  siteIDOptions: PropTypes.arrayOf(PropTypes.string).isRequired
};

const MapSearch = ({ siteData, changeChip, siteIDOptions }) => {
  const center = [50.73, -125.7];

  return (
    <Row style={{ marginTop: "1rem" }}>
      <Col xs={12}>
        <div
          style={{
            height: "600px",
            width: "100%",
            position: "relative"
          }}
        >
          <MapContainer
            style={{ width: "100%", height: "100%" }}
            center={center}
            zoom={7}
            scrollWheelZoom
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <MapSearchControls
              siteData={siteData}
              changeChip={changeChip}
              siteIDOptions={siteIDOptions}
            />
          </MapContainer>
        </div>
      </Col>
    </Row>
  );
};

MapSearch.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  siteData: PropTypes.object.isRequired,
  changeChip: PropTypes.func.isRequired,
  siteIDOptions: PropTypes.arrayOf(PropTypes.string).isRequired
};

export default MapSearch;
